import {useCallback, useContext, useState} from "react";
import {ApiError} from "@common/exception";
import {InventoryReceiveDetail} from "@modules/inbound/inventoryReceiveNotification/model";
import {DateRange} from "@common/model";
import {CommonUtils} from "@modules/inbound/inventoryReceiveNotification/common";
import {RegionContext} from "@common/RegionContext";
import API from "@aws-amplify/api";
import {constructButaneApiName} from "../../../../apiConfig";
import {getAuthorizationHeader} from "@common/Auth";

const START_DATE = "startDate";
const END_DATE = "endDate";
const EMPTY_STRING = "";
const INVENTORY_RECEIVE_DETAILS_KEY = "inventoryReceiveDetails";

export function useGetInventoryReceipts() {
  const [error, setError] = useState<ApiError | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<InventoryReceiveDetail[]>([]);
  const regionContext = useContext(RegionContext);

  const getInventoryReceipts = useCallback(
    async (siteName: string, dateRange?: DateRange) => {
      if (!siteName || !dateRange) {
        return;
      }
      setIsLoading(true);
      setError(null);
      const apiUrl = getApiUrl();
      try {
        const response = await API.get(
          constructButaneApiName(regionContext.region),
          apiUrl, {
            response: true,
            headers: await getAuthorizationHeader()
          }
        );
        let inventoryReceiveDetails = (response?.data[INVENTORY_RECEIVE_DETAILS_KEY] ?? []) as unknown as InventoryReceiveDetail[];
        inventoryReceiveDetails.forEach(inventoryReceiveDetail => {
          inventoryReceiveDetail.siteName = siteName;
          inventoryReceiveDetail.createdAt = inventoryReceiveDetail.createdAt.replace("Z", " UTC");
        });
        setData(inventoryReceiveDetails);
      } catch (exception) {
        const apiError = CommonUtils.getApiError(exception, siteName, 'inventory receipt');
        setError(apiError);
        setData([]);
      }
      setIsLoading(false);

      function getApiUrl() {
        const siteNameUri = `/api/receive/${siteName}/inventory-receipts?`;
        const creationDateUri = `${START_DATE}=${dateRange?.fromDate?.toISOString()}`;
        const getItemDetailsUri = `&itemDetails=false`;
        const apiUriComponents = [siteNameUri, creationDateUri, getItemDetailsUri];
        if (dateRange?.toDate) {
          apiUriComponents.push(`&${END_DATE}=${dateRange?.toDate?.toISOString()}`);
        }
        return apiUriComponents.join(EMPTY_STRING);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [regionContext]);

  return {
    error,
    isLoading,
    inventoryReceipts: data,
    getInventoryReceipts
  };
}